import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../api/axios";

/* ─── Brand palette (shared house tokens) ────────────────────── */
const BRAND = {
  forest: "#0E3A2C", pine: "#1A5440", gold: "#C9952A", honey: "#E8C96A",
  cream: "#FDF6E3", sand: "#F1E6C9", ink: "#15281D", stone: "#4C5C46",
  fail: "#EF5350",
};

/* ─── Filter option sets (match backend contract) ────────────── */
const ACTOR_TYPES = [
  { value: "", label: "All actors" },
  { value: "admin", label: "Admin" },
  { value: "staff", label: "Staff" },
  { value: "system", label: "System" },
];

const ACTIONS = [
  { value: "", label: "All actions" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "login", label: "Login" },
  { value: "logout", label: "Logout" },
  { value: "setting_change", label: "Setting change" },
  { value: "other", label: "Other" },
];

const ENTITIES = [
  { value: "", label: "All entities" },
  { value: "product", label: "Product" },
  { value: "order", label: "Order" },
  { value: "setting", label: "Setting" },
  { value: "staff", label: "Staff" },
  { value: "offer", label: "Offer" },
  { value: "category", label: "Category" },
  { value: "subcategory", label: "Sub-category" },
  { value: "banner", label: "Banner" },
  { value: "stockentry", label: "Stock entry" },
  { value: "tax", label: "Tax" },
  { value: "adminuser", label: "Admin user" },
  { value: "loyalty", label: "Loyalty" },
];

/* ─── Action → chip colours ──────────────────────────────────── */
const actionChipStyle = (action) => {
  switch (action) {
    case "create":         return { bg: "#E4F0EA", color: BRAND.pine,  border: "#9CC7B4" };
    case "update":         return { bg: BRAND.cream, color: BRAND.gold, border: BRAND.honey };
    case "setting_change": return { bg: BRAND.cream, color: BRAND.gold, border: BRAND.honey };
    case "delete":         return { bg: "#FDECEA", color: "#C62828",   border: BRAND.fail };
    case "login":          return { bg: "#E8EEF6", color: "#185fa5",   border: "#B7CDE6" };
    case "logout":         return { bg: "#EFF1ED", color: BRAND.stone, border: "#C9D1C4" };
    default:               return { bg: "#f3f4f6", color: BRAND.ink,   border: "#d1d5db" };
  }
};

/* ─── Date helpers — build local ISO range boundaries ────────── */
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
const addDays    = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
/* Monday-based start of week */
const startOfWeek = (d) => {
  const x = startOfDay(d);
  const day = (x.getDay() + 6) % 7; // 0 = Monday
  return addDays(x, -day);
};

const PRESETS = [
  {
    key: "today", label: "Today",
    range: () => { const n = new Date(); return { from: startOfDay(n), to: endOfDay(n) }; },
  },
  {
    key: "yesterday", label: "Yesterday",
    range: () => { const y = addDays(new Date(), -1); return { from: startOfDay(y), to: endOfDay(y) }; },
  },
  {
    key: "this_week", label: "This week",
    range: () => { const n = new Date(); return { from: startOfWeek(n), to: endOfDay(n) }; },
  },
  {
    key: "last_week", label: "Last week",
    range: () => {
      const sow = startOfWeek(new Date());
      const lastStart = addDays(sow, -7);
      const lastEnd = endOfDay(addDays(sow, -1));
      return { from: lastStart, to: lastEnd };
    },
  },
  {
    key: "this_month", label: "This month",
    range: () => {
      const n = new Date();
      return { from: startOfDay(new Date(n.getFullYear(), n.getMonth(), 1)), to: endOfDay(n) };
    },
  },
];

/* <input type="date"> value (YYYY-MM-DD) in local time */
const toDateInput = (d) => {
  if (!d) return "";
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const date = `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  let h = d.getHours();
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const time = `${String(h).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")} ${period}`;
  return `${date} · ${time}`;
};

const statusColor = (status) => {
  const s = Number(status);
  if (!s) return { color: "#9ca3af", bg: "#f3f4f6", border: "#e5e7eb" };
  if (s >= 500) return { color: "#C62828", bg: "#FDECEA", border: BRAND.fail };
  if (s >= 400) return { color: "#854f0b", bg: "#faeeda", border: "#fac775" };
  if (s >= 200) return { color: BRAND.pine, bg: "#E4F0EA", border: "#9CC7B4" };
  return { color: "#9ca3af", bg: "#f3f4f6", border: "#e5e7eb" };
};

const LIMIT = 20;

const AuditLog = () => {
  /* ── Filter state ── */
  const initialRange = PRESETS[4].range(); // This month
  const [fromDate,   setFromDate]   = useState(toDateInput(initialRange.from));
  const [toDate,     setToDate]     = useState(toDateInput(initialRange.to));
  const [activePreset, setActivePreset] = useState("this_month");
  const [actorType,  setActorType]  = useState("");
  const [action,     setAction]     = useState("");
  const [entity,     setEntity]     = useState("");
  const [q,          setQ]          = useState("");
  const [qInput,     setQInput]     = useState("");

  /* ── Data state ── */
  const [rows,       setRows]       = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: LIMIT, total: 0, totalPages: 1 });
  const [stats,      setStats]      = useState(null);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [exporting,  setExporting]  = useState(false);

  /* Build the from/to ISO params — from = start of fromDate, to = end of toDate */
  const buildDateParams = () => {
    const params = {};
    if (fromDate) params.from = startOfDay(new Date(`${fromDate}T00:00:00`)).toISOString();
    if (toDate)   params.to   = endOfDay(new Date(`${toDate}T00:00:00`)).toISOString();
    return params;
  };

  const filterParams = useMemo(() => {
    const p = { ...buildDateParams() };
    if (actorType) p.actorType = actorType;
    if (action)    p.action    = action;
    if (entity)    p.entity    = entity;
    if (q.trim())  p.q         = q.trim();
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, actorType, action, entity, q]);

  /* ── Debounce the free-text search ── */
  const debRef = useRef(null);
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { setQ(qInput); setPage(1); }, 400);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [qInput]);

  /* ── Reset to page 1 when any filter (except page) changes ── */
  useEffect(() => { setPage(1); }, [fromDate, toDate, actorType, action, entity, q]);

  /* ── Fetch log rows ── */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        // TODO(cutover): no server endpoint for this yet. There is no
        // request-level activity log on the .NET service — docs/API-ROUTES.txt
        // registers nothing under /audit. The nearest thing is
        // audit.AudLoggedActions, a Postgres AFTER-trigger change log over a
        // handful of master tables (schema_name, table_name, row_pk, action
        // I|U|D, actor, at, row_data, changed_fields) with no HTTP method,
        // path, ip, actor type or human summary — a different record, not this
        // one. Program.cs also maps /audit/verify and /audit/seal, but those
        // are hash-chain tamper checks and are not in the route list either.
        const res = await api.get("/audit/log", { params: { ...filterParams, page, limit: LIMIT } });
        if (cancelled) return;
        const data = res.data?.data || [];
        const pg = res.data?.pagination || { page, limit: LIMIT, total: data.length, totalPages: 1 };
        setRows(data);
        setPagination(pg);
      } catch (err) {
        if (cancelled) return;
        setRows([]);
        /* A 404 here is not a fault to chase: the route genuinely does not
           exist on this server (see the TODO above). Say so, rather than
           leaving an operator reading "Failed to load" as an outage. */
        setError(
          err.response?.status === 404
            ? "There is no audit-log endpoint on this server yet — the request activity log has not been ported."
            : err.response?.data?.message || "Failed to load audit log"
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filterParams, page]);

  /* ── Fetch stats for the current filter window ── */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // TODO(cutover): no server endpoint for this yet — nothing aggregates
        // the change log, so the counts strip has no source.
        const res = await api.get("/audit/stats", { params: filterParams });
        if (cancelled) return;
        setStats(res.data?.data || res.data || null);
      } catch {
        if (!cancelled) setStats(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filterParams]);

  /* ── Preset click ── */
  const applyPreset = (preset) => {
    const { from, to } = preset.range();
    setFromDate(toDateInput(from));
    setToDate(toDateInput(to));
    setActivePreset(preset.key);
  };

  const onManualDate = (setter) => (e) => { setter(e.target.value); setActivePreset(""); };

  const resetFilters = () => {
    const r = PRESETS[4].range();
    setFromDate(toDateInput(r.from));
    setToDate(toDateInput(r.to));
    setActivePreset("this_month");
    setActorType("");
    setAction("");
    setEntity("");
    setQ("");
    setQInput("");
    setPage(1);
  };

  /* ── CSV export through the authed client (blob download) ── */
  const handleExport = async () => {
    setExporting(true);
    try {
      // TODO(cutover): no server endpoint for this yet — the only CSV the
      // .NET service produces is /admin/orders/gst-report, a different report.
      const res = await api.get("/audit/export.csv", {
        params: filterParams,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${toDateInput(new Date())}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(
        err.response?.status === 404
          ? "There is no audit-log CSV endpoint on this server yet"
          : err.response?.data?.message || "Failed to export CSV"
      );
    } finally {
      setExporting(false);
    }
  };

  /* ── Normalise stats.byAction into an ordered array of {action,count} ── */
  const actionStats = useMemo(() => {
    const raw = stats?.byAction;
    if (!raw) return [];
    let list = [];
    if (Array.isArray(raw)) {
      list = raw.map((r) => ({ action: r._id ?? r.action ?? r.key, count: r.count ?? r.total ?? 0 }));
    } else if (typeof raw === "object") {
      list = Object.entries(raw).map(([k, v]) => ({ action: k, count: v }));
    }
    return list.filter((r) => r.action).sort((a, b) => b.count - a.count);
  }, [stats]);

  const totalCount = useMemo(() => {
    if (typeof stats?.total === "number") return stats.total;
    return actionStats.reduce((sum, r) => sum + (r.count || 0), 0);
  }, [stats, actionStats]);

  const selectStyle = {
    padding: "9px 12px", borderRadius: 10, border: "1.5px solid var(--mly-border)",
    fontSize: 13, outline: "none", color: "var(--mly-text)", background: "var(--mly-surface)",
    cursor: "pointer", fontFamily: "inherit", minWidth: 140,
  };
  const dateInputStyle = {
    padding: "9px 12px", borderRadius: 10, border: "1.5px solid var(--mly-border)",
    fontSize: 13, outline: "none", color: "var(--mly-text)", background: "var(--mly-surface)", fontFamily: "inherit",
  };

  const th = { padding: "11px 16px", fontSize: 11, fontWeight: 700, color: "var(--mly-text-muted)",
    letterSpacing: 0.5, textTransform: "uppercase", borderBottom: "1px solid var(--mly-border-soft)",
    textAlign: "left", whiteSpace: "nowrap" };

  const actorLabel = (r) => {
    const name = r.actorName || r.actorId || "—";
    return name;
  };

  return (
    <div>
      <div className="container-fluid" style={{ marginBottom: 20, top: 10, position: "relative" }}>
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>

          {/* ── Header ── */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "18px 22px 14px", borderBottom: "1px solid var(--mly-border-soft)", gap: 12, flexWrap: "wrap",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 12, background: BRAND.cream,
                border: `1px solid ${BRAND.honey}`, display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={BRAND.gold}
                  strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <polyline points="9 12 11 14 15 10"/>
                </svg>
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: "var(--mly-text)" }}>Audit Log</h2>
                <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "var(--mly-text-muted)" }}>
                  Every back-office change, login and setting update — searchable & exportable
                </p>
              </div>
            </div>
            <button
              onClick={handleExport}
              disabled={exporting}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "10px 18px", borderRadius: 10, border: "none",
                background: exporting ? "#1A5440aa" : BRAND.forest, color: BRAND.cream,
                fontSize: 13, fontWeight: 700, cursor: exporting ? "not-allowed" : "pointer",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => { if (!exporting) e.currentTarget.style.background = BRAND.pine; }}
              onMouseLeave={(e) => { if (!exporting) e.currentTarget.style.background = BRAND.forest; }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#C9952A"
                strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          </div>

          {/* ── Stats strip (counts by action) ── */}
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 10, alignItems: "stretch",
            padding: "14px 22px", background: "var(--mly-surface-2)", borderBottom: "1px solid var(--mly-border-soft)",
          }}>
            <div style={{
              display: "flex", flexDirection: "column", justifyContent: "center",
              padding: "8px 16px", borderRadius: 12, background: BRAND.forest, color: BRAND.cream,
              minWidth: 96,
            }}>
              <span style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{totalCount}</span>
              <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", opacity: 0.85, marginTop: 4 }}>
                Total events
              </span>
            </div>
            {actionStats.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", color: "var(--mly-text-muted)", fontSize: 12.5 }}>
                No activity in this window
              </div>
            ) : (
              actionStats.map((s) => {
                const c = actionChipStyle(s.action);
                return (
                  <div key={s.action} style={{
                    display: "flex", flexDirection: "column", justifyContent: "center",
                    padding: "8px 16px", borderRadius: 12, background: c.bg,
                    border: `1px solid ${c.border}`, minWidth: 90,
                  }}>
                    <span style={{ fontSize: 18, fontWeight: 800, lineHeight: 1, color: c.color }}>{s.count}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "capitalize", color: c.color, opacity: 0.85, marginTop: 4 }}>
                      {String(s.action).replace(/_/g, " ")}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* ── Filter bar ── */}
          <div style={{ padding: "14px 22px", borderBottom: "1px solid var(--mly-border-soft)", display: "flex", flexDirection: "column", gap: 12 }}>

            {/* Date presets */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              {PRESETS.map((p) => {
                const isActive = activePreset === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => applyPreset(p)}
                    style={{
                      padding: "6px 14px", fontSize: 12, fontWeight: isActive ? 700 : 600,
                      cursor: "pointer", borderRadius: 20,
                      border: `1.5px solid ${isActive ? BRAND.gold : "var(--mly-border)"}`,
                      background: isActive ? BRAND.gold : "var(--mly-surface)",
                      color: isActive ? "#fff" : "var(--mly-text-soft)",
                      whiteSpace: "nowrap", transition: "all 0.15s",
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            {/* Row: date inputs + selects + search */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--mly-text-muted)", letterSpacing: 0.5 }}>FROM</label>
                <input type="date" value={fromDate} max={toDate || undefined}
                  onChange={onManualDate(setFromDate)} style={dateInputStyle} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--mly-text-muted)", letterSpacing: 0.5 }}>TO</label>
                <input type="date" value={toDate} min={fromDate || undefined}
                  onChange={onManualDate(setToDate)} style={dateInputStyle} />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--mly-text-muted)", letterSpacing: 0.5 }}>ACTOR</label>
                <select value={actorType} onChange={(e) => setActorType(e.target.value)} style={selectStyle}>
                  {ACTOR_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--mly-text-muted)", letterSpacing: 0.5 }}>ACTION</label>
                <select value={action} onChange={(e) => setAction(e.target.value)} style={selectStyle}>
                  {ACTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--mly-text-muted)", letterSpacing: 0.5 }}>ENTITY</label>
                <select value={entity} onChange={(e) => setEntity(e.target.value)} style={selectStyle}>
                  {ENTITIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1, minWidth: 220 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--mly-text-muted)", letterSpacing: 0.5 }}>SEARCH</label>
                <div style={{ position: "relative" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="search-icon" aria-hidden="true"><path d="M10.6 2.4a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4Zm0 2.2a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm5.9 11.3 4.8 4.8a1.2 1.2 0 0 1-1.7 1.7l-4.8-4.8a9.6 9.6 0 0 0 1.7-1.7Z" /></svg>
                  <input
                    type="text"
                    placeholder="Search summary, actor or path…"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    style={{
                      paddingLeft: 34, paddingRight: 12, paddingTop: 9, paddingBottom: 9,
                      borderRadius: 10, border: "1.5px solid var(--mly-border)", fontSize: 13,
                      width: "100%", outline: "none", background: "var(--mly-surface)", color: "var(--mly-text)", fontFamily: "inherit",
                    }}
                  />
                </div>
              </div>

              <button
                onClick={resetFilters}
                style={{
                  padding: "9px 16px", borderRadius: 10, border: "1.5px solid var(--mly-border)",
                  background: "var(--mly-surface-2)", color: "var(--mly-text-soft)", fontSize: 13, fontWeight: 700,
                  cursor: "pointer", whiteSpace: "nowrap", height: 38,
                }}
              >
                Reset
              </button>
            </div>
          </div>

          {/* ── Table ── */}
          <div style={{ overflowX: "auto" }}>
            {loading && rows.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 0", color: "var(--mly-text-muted)", fontSize: 14 }}>
                Loading audit log…
              </div>
            ) : error ? (
              <div style={{ textAlign: "center", padding: "48px 0", color: "#ef4444", fontSize: 14 }}>
                {error}
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "var(--mly-surface-2)" }}>
                    <th style={{ ...th, minWidth: 70 }}>#</th>
                    <th style={{ ...th, minWidth: 150 }}>Time</th>
                    <th style={{ ...th, minWidth: 150 }}>Actor</th>
                    <th style={{ ...th, minWidth: 110 }}>Action</th>
                    <th style={{ ...th, minWidth: 110 }}>Entity</th>
                    <th style={{ ...th, minWidth: 120 }}>Entity id</th>
                    <th style={{ ...th, minWidth: 80 }}>Status</th>
                    <th style={{ ...th, minWidth: 120 }}>IP</th>
                    <th style={{ ...th, minWidth: 260 }}>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "56px 0" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                          <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
                            stroke="var(--mly-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                          </svg>
                          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--mly-text-muted)" }}>No audit entries found</span>
                          <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>Try widening the date range or clearing filters</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const ac = actionChipStyle(r.action);
                      const sc = statusColor(r.status);
                      return (
                        <tr
                          key={r._id || idx}
                          style={{ borderBottom: "1px solid var(--mly-border-soft)" }}
                          onMouseEnter={(e) => e.currentTarget.querySelectorAll("td").forEach((td) => (td.style.background = "var(--mly-surface-2)"))}
                          onMouseLeave={(e) => e.currentTarget.querySelectorAll("td").forEach((td) => (td.style.background = ""))}
                        >
                          <td style={{ padding: "12px 16px", color: "var(--mly-text-muted)", fontSize: 12 }}>
                            {(pagination.page - 1) * (pagination.limit || LIMIT) + idx + 1}
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: 12, color: "var(--mly-text-muted)", whiteSpace: "nowrap" }}>
                            {fmtTime(r.at)}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: "var(--mly-text)" }}>{actorLabel(r)}</div>
                            <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 2, textTransform: "capitalize" }}>
                              {r.actorType || "—"}
                            </div>
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <span style={{
                              background: ac.bg, color: ac.color, border: `1px solid ${ac.border}`,
                              fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                              whiteSpace: "nowrap", textTransform: "capitalize",
                            }}>
                              {String(r.action || "—").replace(/_/g, " ")}
                            </span>
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: 12.5, color: "var(--mly-text-soft)", textTransform: "capitalize" }}>
                            {r.entity || "—"}
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: 11.5, color: "var(--mly-text-muted)", fontFamily: "monospace" }}
                            title={r.entityId || ""}>
                            {r.entityId
                              ? (String(r.entityId).length > 10 ? `${String(r.entityId).slice(-8)}…` : r.entityId)
                              : "—"}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <span style={{
                              background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                              fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 8,
                            }}>
                              {r.status ?? "—"}
                            </span>
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: 11.5, color: "var(--mly-text-muted)", fontFamily: "monospace" }}>
                            {r.ip || "—"}
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: 12.5, color: "var(--mly-text-soft)", maxWidth: 360 }}>
                            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                              title={r.summary || ""}>
                              {r.summary || "—"}
                            </div>
                            {(r.method || r.path) && (
                              <div style={{ fontSize: 10.5, color: "var(--mly-text-muted)", marginTop: 2, fontFamily: "monospace" }}>
                                {r.method ? `${r.method} ` : ""}{r.path || ""}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Pagination ── */}
          {pagination.totalPages > 1 && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 22px", borderTop: "1px solid var(--mly-border-soft)", flexWrap: "wrap", gap: 10,
            }}>
              <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
                Page {pagination.page} of {pagination.totalPages} &nbsp;·&nbsp; {pagination.total} events
              </span>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  style={{
                    padding: "6px 14px", borderRadius: 8, border: "1px solid var(--mly-border)", background: "var(--mly-surface)",
                    color: page <= 1 ? "var(--mly-text-muted)" : "var(--mly-text-soft)", fontSize: 12,
                    cursor: page <= 1 ? "not-allowed" : "pointer",
                  }}
                >← Prev</button>
                <span style={{ fontSize: 12, color: "var(--mly-text-soft)", fontWeight: 600, padding: "0 6px" }}>
                  {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  style={{
                    padding: "6px 14px", borderRadius: 8, border: "1px solid var(--mly-border)", background: "var(--mly-surface)",
                    color: page >= pagination.totalPages ? "var(--mly-text-muted)" : "var(--mly-text-soft)", fontSize: 12,
                    cursor: page >= pagination.totalPages ? "not-allowed" : "pointer",
                  }}
                >Next →</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
